export type ExternalBrowserEscapeResult = 'opened' | 'copied' | 'failed';

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

function toSafariScheme(url: string): string {
  if (url.startsWith('https://')) {
    return `x-safari-https://${url.slice('https://'.length)}`;
  }
  if (url.startsWith('http://')) {
    return `x-safari-http://${url.slice('http://'.length)}`;
  }
  return `x-safari-https://${url}`;
}

function toAndroidIntent(url: string): string {
  const path = url.replace(/^https?:\/\//, '');
  return `intent://${path}#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;end`;
}

/** Instagram / Threads register this scheme; the native app opens Safari. */
function toInstagramExternalBrowser(url: string): string {
  return `instagram://extbrowser/?url=${encodeURIComponent(url)}`;
}

export function buildExternalBrowserUrl(targetUrl: string, appKey: string | null): string {
  if (isAndroid()) {
    return toAndroidIntent(targetUrl);
  }
  if (appKey === 'instagram' || appKey === 'threads') {
    return toInstagramExternalBrowser(targetUrl);
  }
  return toSafariScheme(targetUrl);
}

/**
 * Try to open the current page (or given URL) in the device default browser.
 * Must be called from a user gesture (button click). Meta/IG IAB often blocks
 * automatic redirects — a tap + window.open is the most reliable path.
 */
export function openInExternalBrowser(
  appKey: string | null,
  targetUrl: string = window.location.href
): ExternalBrowserEscapeResult {
  const escapeUrl = buildExternalBrowserUrl(targetUrl, appKey);

  try {
    if (appKey === 'instagram' || appKey === 'threads') {
      window.location.href = escapeUrl;
      return 'opened';
    }

    // Facebook, Messenger, and most iOS IABs: user-gesture window.open with x-safari-https
    const popup = window.open(escapeUrl, '_blank');
    if (popup) {
      return 'opened';
    }

    // Fallback: plain URL in new tab (some WebViews allow this)
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
    return 'opened';
  } catch {
    return 'failed';
  }
}

export async function copyPageLink(targetUrl: string = window.location.href): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(targetUrl);
      return true;
    }
  } catch {
    // fall through to legacy copy
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = targetUrl;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
